import { Link } from "@tanstack/react-router"
import { ChevronRightIcon, LeafIcon, ShieldIcon } from "lucide-react"
import type { ComponentProps, ReactNode } from "react"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
} from "@/components/ui/sidebar"
import type {
  OperatorShellNavigationItemState,
  OperatorShellSecondaryNavigationItemState,
} from "@/features/operator/shared/operator-shell-navigation"

export interface OperatorShellIdentitySummary {
  displayName: string
  roleLabel: string
  scopeLabel: string
}

interface OperatorAppSidebarProps extends ComponentProps<typeof Sidebar> {
  brandHref?: string
  brandName?: string
  brandTagline?: string
  footerContent?: ReactNode
  navigationGroupLabel?: string
  navigation: readonly OperatorShellNavigationItemState[]
  secondaryNavigation?: readonly OperatorShellSecondaryNavigationItemState[]
  user: OperatorShellIdentitySummary
}

function getInitials(value: string) {
  const segments = value.trim().split(/\s+/).filter(Boolean).slice(0, 2)

  if (segments.length === 0) {
    return "OP"
  }

  return segments.map((segment) => segment.charAt(0).toUpperCase()).join("")
}

function OperatorSidebarAccount({
  user,
}: {
  user: OperatorShellIdentitySummary
}) {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          size="lg"
          className="h-auto min-h-12 items-start rounded-xl border border-sidebar-border/70 bg-sidebar/60 px-3 py-3"
        >
          <Link to="/account">
            <Avatar className="rounded-lg" size="lg">
              <AvatarFallback className="rounded-lg">
                {getInitials(user.displayName)}
              </AvatarFallback>
            </Avatar>
            <div className="grid min-w-0 flex-1 gap-0.5 text-left leading-tight">
              <span className="truncate text-sm font-medium">
                {user.displayName}
              </span>
              <span className="truncate text-xs text-sidebar-foreground/70">
                {user.scopeLabel}
              </span>
              <span className="truncate text-xs text-sidebar-foreground/55">
                {user.roleLabel}
              </span>
            </div>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

export function OperatorAppSidebar({
  brandHref = "/",
  brandName = "Traffic Ops",
  brandTagline = "Operator workspace",
  footerContent,
  navigationGroupLabel = "Operator surfaces",
  navigation,
  secondaryNavigation = [],
  user,
  ...props
}: OperatorAppSidebarProps) {
  const BrandIcon =
    brandName === "GrowBetter" ? LeafIcon : ShieldIcon

  return (
    <Sidebar collapsible="icon" variant="inset" {...props}>
      <SidebarHeader className="gap-3 p-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              size="lg"
              className="h-auto min-h-12 items-start rounded-xl border border-sidebar-border/70 bg-sidebar/60 px-3 py-3"
            >
              <Link to={brandHref}>
                <div className="flex size-10 items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground">
                  <BrandIcon />
                </div>
                <div className="grid min-w-0 flex-1 gap-0.5 text-left leading-tight">
                  <span className="truncate text-sm font-medium">
                    {brandName}
                  </span>
                  <span className="truncate text-xs text-sidebar-foreground/70">
                    {brandTagline}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarSeparator />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{navigationGroupLabel}</SidebarGroupLabel>
          <SidebarMenu>
            {navigation.map((item) => (
              <Collapsible
                key={item.to}
                asChild
                defaultOpen={item.isActive || item.hasActiveChild}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={item.isActive || item.hasActiveChild}
                    tooltip={item.label}
                  >
                    <Link to={item.to}>
                      <item.icon />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                  {item.items?.length ? (
                    <>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuAction
                          className="data-[state=open]:rotate-90"
                          showOnHover
                        >
                          <ChevronRightIcon />
                          <span className="sr-only">Toggle {item.label}</span>
                        </SidebarMenuAction>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.items.map((child) => (
                            <SidebarMenuSubItem key={child.to}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={child.isActive}
                              >
                                <Link to={child.to}>
                                  <span>{child.label}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </>
                  ) : null}
                </SidebarMenuItem>
              </Collapsible>
            ))}
          </SidebarMenu>
        </SidebarGroup>
        {secondaryNavigation.length ? (
          <SidebarGroup className="mt-auto pt-0">
            <SidebarSeparator className="mb-2" />
            <SidebarGroupLabel>Shortcuts</SidebarGroupLabel>
            <SidebarMenu>
              {secondaryNavigation.map((item) => (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton
                    asChild
                    isActive={item.isActive}
                    tooltip={item.label}
                  >
                    <Link to={item.to}>
                      <item.icon />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ) : null}
      </SidebarContent>
      <SidebarFooter className="p-3">
        {footerContent ?? <OperatorSidebarAccount user={user} />}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
